import * as React from "react"
import CodeMirror, { EditorView } from "@uiw/react-codemirror"
import { json } from "@codemirror/lang-json"
import { xml } from "@codemirror/lang-xml"
import { useTheme } from "../ThemeContext"
import { cloverhoundPalette } from "@lib/design-tokens"

const baseExtension = EditorView.theme({
  "&": { fontSize: "12px" },
  ".cm-content": { fontFamily: "var(--font-mono)" },
  ".cm-gutters": { fontFamily: "var(--font-mono)" },
})

/**
 * CodeMirror wrapper themed with cloverhound tokens. Used for request bodies,
 * mock response templates, and (read-only) response viewing.
 */
export function CodeEditor({
  value,
  onChange,
  language,
  readOnly = false,
  placeholder,
  minHeight = "120px",
  maxHeight,
  className,
}: {
  value: string
  onChange?: (value: string) => void
  language?: "json" | "xml" | "text"
  readOnly?: boolean
  placeholder?: string
  minHeight?: string
  maxHeight?: string
  className?: string
}) {
  const { isDark } = useTheme()

  const extensions = React.useMemo(() => {
    const exts = [baseExtension, EditorView.lineWrapping]
    if (language === "json") exts.push(json())
    else if (language === "xml") exts.push(xml())
    return exts
  }, [language])

  const themeConfig = React.useMemo(
    () =>
      EditorView.theme(
        {
          "&": {
            backgroundColor: "transparent",
            color: isDark ? cloverhoundPalette.gray[50] : cloverhoundPalette.gray[900],
          },
          ".cm-gutters": {
            backgroundColor: "transparent",
            color: isDark ? cloverhoundPalette.gray[600] : cloverhoundPalette.gray[500],
            border: "none",
          },
          ".cm-activeLine": {
            backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)",
          },
          ".cm-activeLineGutter": { backgroundColor: "transparent" },
          "&.cm-focused": { outline: "none" },
          ".cm-selectionBackground, &.cm-focused .cm-selectionBackground": {
            backgroundColor: isDark ? "rgba(94,105,193,0.35) !important" : "rgba(109,120,213,0.25) !important",
          },
          ".cm-cursor": {
            borderLeftColor: isDark ? cloverhoundPalette.gray[50] : cloverhoundPalette.gray[900],
          },
        },
        { dark: isDark },
      ),
    [isDark],
  )

  return (
    <div
      className={
        "overflow-hidden rounded-md border border-cloverhound-gray-250 bg-white dark:border-cloverhound-gray-800 dark:bg-cloverhound-gray-950 " +
        (className ?? "")
      }
    >
      <CodeMirror
        value={value}
        onChange={onChange}
        readOnly={readOnly}
        editable={!readOnly}
        placeholder={placeholder}
        minHeight={minHeight}
        maxHeight={maxHeight}
        theme={isDark ? "dark" : "light"}
        extensions={[...extensions, themeConfig]}
        basicSetup={{
          lineNumbers: true,
          foldGutter: false,
          highlightActiveLine: !readOnly,
          searchKeymap: true,
        }}
      />
    </div>
  )
}
